/**
 * Approved salespeople and business owners for tagging a sale so reminders
 * reach the right person. Accountants and owners can list org users.
 */

import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useOrganization } from '../../../contexts/OrganizationContext';

const isAssignableSalesUser = (orgUser) =>
  orgUser.approved === true &&
  (orgUser.role === 'Salesperson' || orgUser.role === 'BusinessOwner');

const displayName = (orgUser) => orgUser.fullName || orgUser.email || orgUser.id;

export const useSalespeople = () => {
  const { organizationId, userRole } = useOrganization();
  const [salespeople, setSalespeople] = useState([]);

  useEffect(() => {
    const canListUsers = userRole === 'BusinessOwner' || userRole === 'Accountant';
    if (!organizationId || !canListUsers) {
      setSalespeople([]);
      return undefined;
    }

    let active = true;

    const loadSalespeople = async () => {
      try {
        const snapshot = await getDocs(collection(db, `organizations/${organizationId}/users`));
        if (!active) return;

        setSalespeople(
          snapshot.docs
            .map((userDoc) => ({ id: userDoc.id, ...userDoc.data() }))
            .filter(isAssignableSalesUser)
            .sort((a, b) => {
              if (a.role !== b.role) {
                return a.role === 'BusinessOwner' ? -1 : 1;
              }
              return displayName(a).localeCompare(displayName(b));
            })
            .map((orgUser) => ({
              id: orgUser.id,
              fullName: displayName(orgUser),
              email: orgUser.email,
              role: orgUser.role
            }))
        );
      } catch (loadError) {
        if (active) setSalespeople([]);
      }
    };

    loadSalespeople();

    return () => {
      active = false;
    };
  }, [organizationId, userRole]);

  return salespeople;
};

export default useSalespeople;
